import React from "react";
import { Formik, useFormikContext } from 'formik';
import { TextField } from "../components/forms/TextField";
import { Form } from "../components/forms/Form";
import ButtonWithLoading from "../components/forms/SubmitButton";
import { useNavigate, useParams } from "react-router-dom";
import { DeleteFlow } from "../components/DeleteFlow";
import { AppContext } from "../AppContext";
import {
  GetEmailAccount,
  GetEmailAccountResult,
  UpdateEmailAccount,
  UpdateEmailAccountResult,
  DeleteEmailAccount,
  DeleteEmailAccountResult,
} from "bloggers-backend/lib/email/domain/events";

interface Values {
  id: string
  email: string
  service: string
  senderName: string
  signature: string
}

const emptyValues: Values = { id: '', email: '', service: '', senderName: '', signature: '' };

function toValues(account: {id: string, email: string, service: string, senderName?: string, signature?: string}): Values {
  return {
    ...emptyValues,
    ...account,
    senderName: account.senderName ?? '',
    signature: account.signature ?? '',
  };
}

export const EmailAccountPage = () => {
  const {notificationsService, client} = React.useContext(AppContext);
  const [submitting, setSubmitting] = React.useState(false);
  const navigate = useNavigate();

  async function save(values: Values, resetForm: (state: {values: Values}) => void) {
    setSubmitting(true);

    const result = await client<UpdateEmailAccount, UpdateEmailAccountResult>({
      event: 'updateEmailAccount',
      data: {id: values.id, senderName: values.senderName, signature: values.signature}
    });

    if (result.success) {
      resetForm({values: toValues(result.value)});
      notificationsService.notify({severity: 'success', message: 'Saved'});
    } else {
      notificationsService.notify({severity: 'error', message: JSON.stringify(result.errors)});
    }

    setSubmitting(false);
  }

  async function deleteAccount(id: string) {
    const result = await client<DeleteEmailAccount, DeleteEmailAccountResult>({
      event: 'deleteEmailAccount',
      data: {id}
    });

    if (result.success) {
      notificationsService.notify({severity: 'success', message: 'Email account deleted'});
      navigate('/email-accounts');
    } else {
      const message = Object.values(result.errors).flat().join(', ') || result.reason;
      notificationsService.notify({severity: 'error', message});
    }
  }

  return <Formik<Values>
    initialValues={emptyValues}
    onSubmit={(values, {resetForm}) => save(values, resetForm)}
  >
    {({submitForm, values}) => (
      <Form>
        <h2>Email account</h2>
        <TextField disabled name="email" label="email"/>
        <TextField disabled name="service" label="service"/>
        <TextField name="senderName" label="sender name"/>
        <TextField name="signature" label="signature" multiline minRows={3}/>
        <ButtonWithLoading loading={submitting} onClick={submitForm}/>
        <DeleteFlow deleteWhat="email account" deleteAction={() => deleteAccount(values.id)}/>
        <DataLoader/>
      </Form>
    )}
  </Formik>
};

const DataLoader = () => {
  const {id} = useParams();
  const { resetForm } = useFormikContext<Values>();
  const {notificationsService, client} = React.useContext(AppContext)
  const [loading, setLoading] = React.useState(false);

  async function loadData(id: string) {
    setLoading(true);
    const result = await client<GetEmailAccount, GetEmailAccountResult>({
      event: 'getEmailAccount',
      data: {id}
    });

    if (result.success) {
      resetForm({values: toValues(result.value)})
    } else {
      notificationsService.notify({
        severity: 'error',
        message: JSON.stringify(result.errors)
      })
    }

    setLoading(false)
  }

  React.useEffect(() => {
    if (!id || loading) {
      return;
    }


    loadData(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return null
}
